import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaEnvelope,
  FaLock,
  FaUser,
  FaSignInAlt,
  FaUserPlus,
} from "react-icons/fa";
import Logo from "../assets/LightIdeaPlayerLogoStack.png";

const LOGIN_THEME = {
  shell: "bg-gradient-to-br from-[#180303] via-[#2a0707] to-[#0f0202]",
  frame: "shadow-[0_0_90px_rgba(255,59,59,0.18)]",
  accentBar: "bg-[linear-gradient(90deg,#ff1f1f_0%,#ff3b3b_45%,#b30000_100%)]",
  accentSoft: "bg-red-500/20",
  accentBorder: "border-red-300/30",
  pill: "bg-white/10",
  panelStrong: "bg-[#0f0c18]/88",
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorText, setErrorText] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [signupUsername, setSignupUsername] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");

  const nextPath = useMemo(() => {
    return location.state?.from?.pathname || "/profile";
  }, [location.state]);

  useEffect(() => {
    document.title =
      mode === "signup"
        ? "Create account • Light Idea Productions"
        : "Login • Light Idea Productions";
  }, [mode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setErrorText("");

    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail.trim(),
      password: loginPassword,
    });

    setLoading(false);

    if (error) {
      setErrorText(error.message || "Unable to sign in.");
      return;
    }

    navigate(nextPath, { replace: true });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setErrorText("");

    const username = signupUsername.trim();
    const email = signupEmail.trim();

    if (!username) {
      setLoading(false);
      setErrorText("Username is required.");
      return;
    }

    if (username.length < 3) {
      setLoading(false);
      setErrorText("Username must be at least 3 characters.");
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password: signupPassword,
      options: {
        data: {
          username,
        },
      },
    });

    setLoading(false);

    if (error) {
      setErrorText(error.message || "Unable to create account.");
      return;
    }

    if (data?.session) {
      navigate(nextPath, { replace: true });
      return;
    }

    setMessage(
      "Account created. If email confirmation is enabled in Supabase, confirm your email, then log in.",
    );
    setMode("login");
    setLoginEmail(email);
    setLoginPassword("");
    setSignupPassword("");
  };

  return (
    <div className={`min-h-screen ${LOGIN_THEME.shell} text-white`}>
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.16),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.14),transparent_30%)]" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-[1800px] items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full max-w-6xl overflow-hidden rounded-[28px] border border-white/10 bg-black/30 backdrop-blur-xl lg:grid-cols-[1.05fr_0.95fr]">
          <div className="relative hidden min-h-[620px] overflow-hidden lg:block">
            <div className={`absolute inset-0 ${LOGIN_THEME.frame}`} />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.20),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.20),transparent_30%)]" />
            <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/60" />

            <div className="relative z-10 flex h-full flex-col p-10">
              <div>
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-white/90 backdrop-blur-md">
                  Light Idea Productions
                </div>

                <h1 className="max-w-xl text-5xl font-semibold tracking-tight leading-tight">
                  Secure access for your needs.
                </h1>

                <p className="mt-4 max-w-xl text-base leading-7 text-white/75">
                  Sign in to continue, or create a new account with your
                  username, email, and password.
                </p>

                <div className="mt-6 h-1.5 w-44 overflow-hidden rounded-full">
                  <div className={`h-full w-full ${LOGIN_THEME.accentBar}`} />
                </div>
              </div>

              <div className="flex flex-1 items-center justify-center px-8 pb-6 pt-10">
                <img
                  src={Logo}
                  alt="Light Idea Productions"
                  className="max-h-[300px] w-full max-w-[420px] object-contain opacity-90 drop-shadow-[0_0_30px_rgba(0,0,0,0.45)]"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center p-4 sm:p-6 lg:p-8">
            <div
              className={`w-full max-w-xl rounded-[26px] border border-white/10 ${LOGIN_THEME.panelStrong} p-5 shadow-2xl backdrop-blur-xl sm:p-7`}
            >
              <div className="mb-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setMode("login");
                    setMessage("");
                    setErrorText("");
                  }}
                  className={`min-w-[130px] rounded-full px-4 py-2 text-sm font-medium transition ${
                    mode === "login"
                      ? "bg-white text-black"
                      : "border border-white/15 bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  <span className="inline-flex w-full items-center justify-center gap-2">
                    <FaSignInAlt />
                    Login
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("signup");
                    setMessage("");
                    setErrorText("");
                  }}
                  className={`min-w-[130px] rounded-full px-4 py-2 text-sm font-medium transition ${
                    mode === "signup"
                      ? "bg-white text-black"
                      : "border border-white/15 bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  <span className="inline-flex w-full items-center justify-center gap-2">
                    <FaUserPlus />
                    Sign up
                  </span>
                </button>
              </div>

              <div className="mb-6 min-h-[84px]">
                <div className="mb-2 text-[11px] uppercase tracking-[0.28em] text-white/45">
                  Authentication
                </div>
                <h2 className="text-3xl font-semibold tracking-tight">
                  {mode === "signup" ? "Create account" : "Welcome back"}
                </h2>
                <p className="mt-2 text-sm leading-6 text-white/68">
                  {mode === "signup"
                    ? "Create your account to access the platform."
                    : "Sign in using your email and password."}
                </p>
              </div>

              {message ? (
                <div
                  className={`mb-4 rounded-2xl border px-4 py-3 text-sm text-white/90 ${LOGIN_THEME.accentBorder} ${LOGIN_THEME.accentSoft}`}
                >
                  {message}
                </div>
              ) : null}

              {errorText ? (
                <div className="mb-4 rounded-2xl border border-red-400/30 bg-red-500/15 px-4 py-3 text-sm text-white/90">
                  {errorText}
                </div>
              ) : null}

              <div className="min-h-[320px]">
                {mode === "login" ? (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <Field
                      icon={<FaEnvelope />}
                      label="Email"
                      type="email"
                      value={loginEmail}
                      onChange={setLoginEmail}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />

                    <Field
                      icon={<FaLock />}
                      label="Password"
                      type="password"
                      value={loginPassword}
                      onChange={setLoginPassword}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />

                    <div className="pt-[72px]">
                      <button
                        type="submit"
                        disabled={loading}
                        className="mt-2 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:scale-[1.01] hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaSignInAlt />
                        <span>{loading ? "Signing in..." : "Login"}</span>
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSignup} className="space-y-4">
                    <Field
                      icon={<FaUser />}
                      label="Username"
                      type="text"
                      value={signupUsername}
                      onChange={setSignupUsername}
                      placeholder="Choose a username"
                      autoComplete="username"
                    />

                    <Field
                      icon={<FaEnvelope />}
                      label="Email"
                      type="email"
                      value={signupEmail}
                      onChange={setSignupEmail}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />

                    <Field
                      icon={<FaLock />}
                      label="Password"
                      type="password"
                      value={signupPassword}
                      onChange={setSignupPassword}
                      placeholder="Create a password"
                      autoComplete="new-password"
                    />

                    <button
                      type="submit"
                      disabled={loading}
                      className="mt-2 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:scale-[1.01] hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FaUserPlus />
                      <span>{loading ? "Creating account..." : "Sign up"}</span>
                    </button>
                  </form>
                )}
              </div>

              <div className="mt-6 border-t border-white/10 pt-5 text-sm text-white/60">
                <span>Back to </span>
                <Link to="/" className="text-white hover:underline">
                  Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  icon,
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  return (
    <label className="block">
      <div className="mb-2 text-sm font-medium text-white/88">{label}</div>
      <div className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-md transition focus-within:border-white/20 focus-within:bg-white/[0.07]">
        <div className="shrink-0 text-white/55">{icon}</div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/35"
          required
        />
      </div>
    </label>
  );
}
